import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import {
  asId,
  type DisciplineId,
  type JobRoleId,
  type MaterialId,
  type ProjectId,
  type ServiceId,
  type ServiceItemId,
  type UnitTypeId,
} from '@obraplan/core/domain/shared/ids';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import type { IndexOrigin } from '@obraplan/core/domain/takeoff/index-origin';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';
import { materialNaturalKey } from '@obraplan/core/domain/schedule/material';
import type { Discipline } from '@obraplan/core/domain/structure/discipline';
import { JobRoleDisciplineIndex } from '@obraplan/core/domain/workforce/job-role-discipline';
import type { Service, ServiceItem } from '@obraplan/core/domain/takeoff/service';
import { serviceKey } from '@obraplan/core/domain/takeoff/service-catalog';
import {
  approvedServiceLabel,
  type ApprovedService,
} from '@obraplan/core/domain/takeoff/project-budget';
import {
  diffItemQuantities,
  mesmasQuantidades,
  planServiceItems,
  sameItemAsWritten,
  validItemQuantity,
  type ServiceItemPlan,
  type ServiceItemRules,
  type ServiceSheetImport,
  type ServiceSheetQuantities,
  type UnitTypeItemQuantity,
  type UnitTypeReferenceSheet,
} from '@obraplan/core/domain/takeoff/service-items';
import {
  decideInServiceUnit,
  decideMeasured,
  diffServiceQuantities,
  type UnitTypeServiceQuantity,
} from '@obraplan/core/domain/takeoff/service-quantity';
import {
  normalizeSteelRates,
  planSteelRates,
  STEEL_GAUGES,
  type DisciplineSteelRate,
  type SteelGauge,
  type SteelRateAverage,
  type SteelRatesPlan,
} from '@obraplan/core/domain/takeoff/steel';
import type { AppliesTo } from '@obraplan/core/domain/takeoff/applies-to';
import { nomeDaPeca } from '@obraplan/core/domain/takeoff/planilha-por-peca';
import { AppError, validationError } from '../../errors/app-error';
import { saveProjectServiceItemsSchema, type PieceNameAliasView } from '../../contracts/takeoff';
import { parseInput } from '../../contracts/parse';
import type { Clock } from '../../ports/clock';
import type { IdGenerator } from '../../ports/id-generator';
import type { MaterialRepository } from '../../ports/schedule-repositories';
import type { DisciplineRepository } from '../../ports/structure-repositories';
import type {
  DisciplineSteelRateRepository,
  PieceQuantityRecord,
  PieceNameAliasRepository,
  PieceQuantityRepository,
  ProjectBudgetRepository,
  ServiceItemRepository,
  ServiceRepository,
  ServiceSheetRepository,
  UnitTypeItemRepository,
  UnitTypeRepository,
} from '../../ports/takeoff-repositories';
import type {
  JobRoleDisciplineRepository,
  JobRoleRepository,
} from '../../ports/workforce-repositories';
import type { TransactionRunner } from '../../ports/transaction';
import type { AuditService } from '../../services/audit-service';
import type { PermissionChecker } from '../../services/permission-checker';
import type { Session } from '../../session';
import { acoTravadoPorDisciplina, servicosTravados } from './servicos-travados';

/**
 * Os itens do serviço — leitura e gravação (Fase 24, fatia 3).
 *
 * A Fase 14 gravou metade do catálogo: disciplina, serviço, medida e espessura
 * (`ApplyServiceCatalog`). A outra metade — o que cada serviço consome, a planilha de onde
 * veio, a aba que orça cada tipologia e o número que alguém digitou por cima dela —
 * ficou no navegador, porque o item apontava um material e 140 dos 218 itens do cliente
 * não têm código. Com o item podendo ser descrição livre (RN-250), ela sobe (RN-249).
 *
 * São casos de uso **próprios**, e não uma extensão do cadastro de serviços, por uma
 * diferença de contrato: aquele envelope só acrescenta — serviço some por ação com nome —,
 * e este substitui, porque o item que a tela deixou de mandar é o item que alguém tirou
 * (RN-255). Um envelope com as duas regras dentro seria um envelope que ninguém saberia
 * dizer o que apaga.
 */

export interface ServiceItemsDeps {
  readonly services: ServiceRepository;
  readonly disciplines: DisciplineRepository;
  readonly items: ServiceItemRepository;
  readonly sheets: ServiceSheetRepository;
  readonly unitTypeItems: UnitTypeItemRepository;
  /** As taxas de aço de cada disciplina, por obra (Fase 27, `0053`). */
  readonly steelRates: DisciplineSteelRateRepository;
  /** A quantidade pronta de cada peça importada, por tipologia (Fase 28, `0064`). */
  readonly pieceQuantities: PieceQuantityRepository;
  /** O nome da planilha das peças renomeadas (20/10/2026, `0081`). */
  readonly pieceAliases: Pick<PieceNameAliasRepository, 'listByProject'>;
  /** Os serviços aprovados no orçamento, cuja quantidade não muda mais (Fase 33, RN-438). */
  readonly budgets: Pick<ProjectBudgetRepository, 'listApprovedServices'>;
  readonly unitTypes: UnitTypeRepository;
  readonly materials: MaterialRepository;
  readonly jobRoles: JobRoleRepository;
  readonly roleDisciplines: JobRoleDisciplineRepository;
  readonly permissions: PermissionChecker;
  readonly audit: AuditService;
  readonly tx: TransactionRunner;
  readonly clock: Clock;
  readonly ids: IdGenerator;
}

// ── Leitura ─────────────────────────────────────────────────────────────────────

/** Os itens de um serviço nesta obra, com o que a tela precisa para achar o serviço. */
export interface ServiceItemsOfService {
  readonly serviceId: ServiceId;
  /**
   * O nome da disciplina e o do serviço, e não só o id.
   *
   * O catálogo da tela é organizado pela chave natural `(disciplina, serviço)` (RN-164),
   * e a leitura que diz o nome junto não depende de uma segunda consulta chegar na mesma
   * hora — duas leituras de obras diferentes, cruzadas por uma troca de obra no meio,
   * pendurariam os itens de uma no catálogo da outra.
   */
  readonly disciplineName: string;
  readonly serviceName: string;
  /** A unidade em que a planilha mediu o serviço. */
  readonly sheetUnit?: string;
  readonly quantityBySheet: Readonly<Record<string, number>>;
  readonly items: readonly ServiceItem[];
}

/** O que uma tipologia decidiu. */
export interface UnitTypeItemsView {
  readonly unitTypeId: UnitTypeId;
  readonly sheetName?: string;
  readonly quantities: readonly {
    readonly serviceItemId: ServiceItemId;
    readonly quantity: number;
    /** Por que discordou do calculado (Fase 24, fatia 6). */
    readonly reason?: string;
  }[];
  /**
   * O que ela decidiu sobre o **serviço** (Fase 24, fatia 5): o medido digitado — a correção
   * com o motivo, ou o número do serviço que o desenho não mede — e a quantidade na unidade
   * do serviço. Só o decidido; o traçado é da tela, que tem o desenho.
   */
  readonly serviceQuantities?: readonly {
    readonly serviceId: ServiceId;
    readonly measured?: number;
    readonly reason?: string;
    readonly quantity?: number;
  }[];
  /**
   * A quantidade pronta de cada peça importada (Fase 28, RN-384): no item, quando
   * `serviceItemId` vem; no serviço, quando não vem. Ausente = a tipologia não tem peça
   * importada.
   */
  readonly pieceQuantities?: readonly {
    readonly pieceName: string;
    readonly serviceId: ServiceId;
    readonly serviceItemId?: ServiceItemId;
    readonly quantity: number;
  }[];
  /**
   * Os nomes com que a planilha escreve as peças renomeadas (20/10/2026, RN-458) — a
   * reimportação traduz um no outro. Ausente = nenhuma peça da tipologia mudou de nome.
   */
  readonly pieceNameAliases?: readonly PieceNameAliasView[];
}

/**
 * Uma função que pode ser escolhida para um item de mão de obra.
 *
 * Vem com as disciplinas que ela executa (RN-166), e é a tela que filtra pela disciplina
 * do serviço aberto. Vem **nesta** leitura, e não na do quadro de pessoal, porque aquela
 * pede `employees.view`: quem monta o quantitativo precisa escolher "pedreiro" sem
 * precisar enxergar a folha de pagamento da obra.
 */
export interface JobRoleOption {
  readonly id: JobRoleId;
  readonly name: string;
  readonly disciplineIds: readonly DisciplineId[];
}

/**
 * A taxa de aço de uma bitola numa disciplina (Fase 27, RN-367) — pelo **nome** da disciplina,
 * como o serviço: o catálogo da tela é organizado pela chave natural, e é por ela que a taxa
 * volta para a disciplina certa.
 */
export interface SteelRateView {
  readonly disciplineName: string;
  readonly gauge: SteelGauge;
  readonly kgPerM3: number;
  /**
   * O elemento da lista, na disciplina que mistura elementos (Fase 42, RN-573). Ausente: a
   * disciplina inteira.
   */
  readonly element?: AppliesTo;
  /** De onde veio a taxa, e a foto da média (Fase 40, etapa 3 — RN-533). */
  readonly origin?: IndexOrigin;
  readonly average?: SteelRateAverage;
}

export interface ProjectServiceItems {
  readonly projectId: ProjectId;
  readonly sheetImport?: ServiceSheetImport;
  /**
   * Só os serviços que têm item, aba ou unidade de planilha nesta obra — ou número decidido
   * por alguma tipologia, ou quantidade de alguma peça importada (Fase 28), que a tela acha
   * pela chave do serviço vinda daqui.
   */
  readonly services: readonly ServiceItemsOfService[];
  /** Só as tipologias que decidiram alguma coisa — aba, número ou peça importada. */
  readonly unitTypes: readonly UnitTypeItemsView[];
  readonly jobRoles: readonly JobRoleOption[];
  /**
   * As taxas de aço vivas das disciplinas que a obra alcança, na ordem das bitolas (Fase 27).
   * A disciplina sem taxa não aparece — e o aço dela não mede. Opcional porque a resposta de
   * um servidor anterior à `0053` não a traz, e a tela tem de abrir mesmo assim.
   */
  readonly steelRates?: readonly SteelRateView[];
}

/** A posição da bitola na lista fechada — a ordem da tela e do modelo. */
const ORDEM_DA_BITOLA = new Map<SteelGauge, number>(
  STEEL_GAUGES.map((info, indice) => [info.code, indice]),
);

export class GetProjectServiceItems {
  constructor(private readonly deps: ServiceItemsDeps) {}

  async execute(
    projectId: ProjectId,
    session: Session,
  ): Promise<Result<ProjectServiceItems, AppError>> {
    const allowed = this.deps.permissions.ensureForProject(
      session,
      PERMISSION.TAKEOFF_VIEW,
      projectId,
    );
    if (!allowed.ok) return allowed;

    const [
      services,
      disciplines,
      items,
      sheetImport,
      sheetQuantities,
      referenceSheets,
      quantities,
      serviceQuantities,
      roles,
      links,
      taxas,
      pecasGravadas,
      apelidos,
    ] = await Promise.all([
      this.deps.services.listAvailableForProject(projectId),
      this.deps.disciplines.listAvailableForProject(projectId),
      this.deps.items.listByProject(projectId),
      this.deps.sheets.findImport(projectId),
      this.deps.sheets.listQuantities(projectId),
      this.deps.unitTypeItems.listReferenceSheets(projectId),
      this.deps.unitTypeItems.listQuantities(projectId),
      this.deps.unitTypeItems.listServiceQuantities(projectId),
      this.deps.jobRoles.listAll(),
      this.deps.roleDisciplines.listAll(),
      this.deps.steelRates.listByProject(projectId),
      this.deps.pieceQuantities.listByProject(projectId),
      this.deps.pieceAliases.listByProject(projectId),
    ]);

    const disciplinaPorId = new Map(disciplines.map((disciplina) => [disciplina.id, disciplina]));
    const itensPorServico = agrupar(items, (item) => item.serviceId);
    const abaPorServico = new Map(sheetQuantities.map((linha) => [linha.serviceId, linha]));
    const decididos = new Set<ServiceId>(serviceQuantities.map((decisao) => decisao.serviceId));
    const pecas = pecasAlcancaveis(pecasGravadas, services, disciplinaPorId, items);
    const comPecas = new Set<ServiceId>(pecas.map((peca) => peca.serviceId));

    const servicos = services.flatMap((service): ServiceItemsOfService[] => {
      const disciplina = disciplinaPorId.get(service.disciplineId);
      const doServico = itensPorServico.get(service.id) ?? [];
      const daAba = abaPorServico.get(service.id);
      // O serviço que só tem peça importada também vem (Fase 28): é por ele que a tela
      // traduz o id da linha de volta para a chave `(disciplina, serviço)`.
      const semNada =
        doServico.length === 0 &&
        daAba === undefined &&
        !decididos.has(service.id) &&
        !comPecas.has(service.id);
      if (disciplina === undefined || semNada) return [];
      return [
        {
          serviceId: service.id,
          disciplineName: disciplina.name,
          serviceName: service.name,
          quantityBySheet: daAba?.quantityBySheet ?? {},
          items: [...doServico].sort((a, b) => a.position - b.position),
          ...(daAba?.sheetUnit === undefined ? {} : { sheetUnit: daAba.sheetUnit }),
        },
      ];
    });

    const abaPorTipologia = new Map(referenceSheets.map((aba) => [aba.unitTypeId, aba.sheetName]));
    const decisoesPorTipologia = agrupar(quantities, (decisao) => decisao.unitTypeId);
    const servicosPorTipologia = agrupar(serviceQuantities, (decisao) => decisao.unitTypeId);
    const pecasPorTipologia = agrupar(pecas, (peca) => peca.unitTypeId);
    const apelidosPorTipologia = agrupar(apelidos, (apelido) => apelido.unitTypeId);
    const tipologias = [
      ...new Set<UnitTypeId>([
        ...abaPorTipologia.keys(),
        ...decisoesPorTipologia.keys(),
        ...servicosPorTipologia.keys(),
        ...pecasPorTipologia.keys(),
      ]),
    ].map((unitTypeId): UnitTypeItemsView => {
      const sheetName = abaPorTipologia.get(unitTypeId);
      const daTipologia = pecasPorTipologia.get(unitTypeId) ?? [];
      return {
        unitTypeId,
        quantities: (decisoesPorTipologia.get(unitTypeId) ?? []).map((decisao) => ({
          serviceItemId: decisao.serviceItemId,
          quantity: decisao.quantity,
          ...(decisao.reason === undefined ? {} : { reason: decisao.reason }),
        })),
        serviceQuantities: (servicosPorTipologia.get(unitTypeId) ?? []).map((decisao) => ({
          serviceId: decisao.serviceId,
          ...(decisao.measured === undefined ? {} : { measured: decisao.measured }),
          ...(decisao.measuredReason === undefined ? {} : { reason: decisao.measuredReason }),
          ...(decisao.quantity === undefined ? {} : { quantity: decisao.quantity }),
        })),
        ...(sheetName === undefined ? {} : { sheetName }),
        // Ausente, e não lista vazia, na tipologia sem peça importada: é o que o contrato
        // diz, e o que a resposta de um servidor anterior à `0064` já dizia.
        ...(daTipologia.length === 0
          ? {}
          : {
              pieceQuantities: daTipologia.map((peca) => ({
                pieceName: peca.pieceName,
                serviceId: peca.serviceId,
                ...(peca.serviceItemId === undefined ? {} : { serviceItemId: peca.serviceItemId }),
                quantity: peca.quantity,
              })),
            }),
        ...(apelidosPorTipologia.has(unitTypeId)
          ? {
              pieceNameAliases: (apelidosPorTipologia.get(unitTypeId) ?? []).map((apelido) => ({
                sheetName: apelido.sheetName,
                pieceName: apelido.pieceName,
              })),
            }
          : {}),
      };
    });

    // Só as funções que podem ser escolhidas agora, e só as disciplinas desta obra: a
    // disciplina de outra obra não aparece em serviço nenhum daqui.
    const daObra = new Set<DisciplineId>(disciplines.map((disciplina) => disciplina.id));
    const disciplinasDaFuncao = agrupar(
      links.filter((link) => daObra.has(link.disciplineId)),
      (link) => link.jobRoleId,
    );
    const jobRoles = roles
      .filter((role) => role.isActive && role.deletedAt === undefined)
      .map((role): JobRoleOption => ({
        id: role.id,
        name: role.name,
        disciplineIds: (disciplinasDaFuncao.get(role.id) ?? []).map((link) => link.disciplineId),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

    // As taxas vão pelo nome da disciplina, na ordem do cadastro e das bitolas. A da
    // disciplina que a obra não alcança mais fica de fora: não há onde mostrá-la.
    const steelRates = taxas
      .filter((taxa) => taxa.deletedAt === undefined && disciplinaPorId.has(taxa.disciplineId))
      .map((taxa) => ({ taxa, disciplina: disciplinaPorId.get(taxa.disciplineId)! }))
      .sort(
        (a, b) =>
          a.disciplina.position - b.disciplina.position ||
          a.disciplina.name.localeCompare(b.disciplina.name, 'pt-BR') ||
          (a.taxa.element ?? '').localeCompare(b.taxa.element ?? '') ||
          (ORDEM_DA_BITOLA.get(a.taxa.gauge) ?? 0) - (ORDEM_DA_BITOLA.get(b.taxa.gauge) ?? 0),
      )
      .map(({ taxa, disciplina }): SteelRateView => ({
        disciplineName: disciplina.name,
        gauge: taxa.gauge,
        kgPerM3: taxa.kgPerM3,
        ...(taxa.element === undefined ? {} : { element: taxa.element }),
        ...(taxa.origin === undefined ? {} : { origin: taxa.origin }),
        ...(taxa.average === undefined ? {} : { average: taxa.average }),
      }));

    return ok({
      projectId,
      services: servicos,
      unitTypes: tipologias,
      jobRoles,
      steelRates,
      ...(sheetImport === undefined ? {} : { sheetImport }),
    });
  }
}

function agrupar<T, K>(lista: readonly T[], chave: (item: T) => K): Map<K, T[]> {
  const mapa = new Map<K, T[]>();
  for (const item of lista) {
    const grupo = mapa.get(chave(item));
    if (grupo === undefined) mapa.set(chave(item), [item]);
    else grupo.push(item);
  }
  return mapa;
}

/**
 * As quantidades por peça que a tela consegue pendurar em alguma coisa (Fase 28, RN-390).
 *
 * A tela volta da linha para o catálogo pela chave natural, e só conhece o serviço que esta
 * leitura devolve: o que a obra não alcança mais — ou cuja disciplina não está entre as dela —
 * não tem para onde voltar. E a linha de item que saiu, ou de item de outro serviço, contaria
 * um número que ninguém vê nem corrige. As duas ficam de fora; a próxima gravação da tipologia
 * as tira do banco, porque a tela não as manda de volta.
 */
function pecasAlcancaveis(
  pecas: readonly PieceQuantityRecord[],
  services: readonly Service[],
  disciplinaPorId: ReadonlyMap<DisciplineId, Discipline>,
  items: readonly ServiceItem[],
): readonly PieceQuantityRecord[] {
  if (pecas.length === 0) return [];
  const visiveis = new Set<ServiceId>(
    services
      .filter((service) => disciplinaPorId.has(service.disciplineId))
      .map((service) => service.id),
  );
  const servicoDoItem = new Map<ServiceItemId, ServiceId>(
    items.map((item) => [item.id, item.serviceId]),
  );
  return pecas.filter(
    (peca) =>
      visiveis.has(peca.serviceId) &&
      (peca.serviceItemId === undefined ||
        servicoDoItem.get(peca.serviceItemId) === peca.serviceId),
  );
}

// ── Gravação ────────────────────────────────────────────────────────────────────

type Entrada = ReturnType<typeof saveProjectServiceItemsSchema.parse>;

/**
 * O que a gravação fez, para a tela dizer e a auditoria registrar.
 *
 * Os dois `skipped` existem para a corrida que o envelope não controla: o serviço que a
 * tela conhece e o servidor ainda não (a gravação do cadastro não chegou), e a tipologia
 * que outra pessoa removeu entre a leitura e a gravação. Recusar o envelope inteiro por
 * um deles travaria todas as gravações seguintes; pular e dizer o nome é o que deixa a
 * tela avisar sem perder o resto.
 */
export interface ServiceItemsSaved {
  readonly itemsCreated: number;
  readonly itemsUpdated: number;
  readonly itemsRemoved: number;
  readonly quantitiesSet: number;
  readonly quantitiesCleared: number;
  /** As decisões de serviço por tipologia (fatia 5) que mudaram, e as que voltaram ao traçado. */
  readonly serviceQuantitiesSet: number;
  readonly serviceQuantitiesCleared: number;
  /** As taxas de aço que nasceram ou mudaram de número (Fase 27), e as que saíram. */
  readonly steelRatesSet: number;
  readonly steelRatesCleared: number;
  /**
   * As quantidades por peça (Fase 28) que nasceram ou mudaram de número, e as que saíram —
   * a da peça que não veio na tipologia descrita, e a do item que saiu do catálogo.
   */
  readonly pieceQuantitiesSet: number;
  readonly pieceQuantitiesCleared: number;
  readonly skippedServices: readonly string[];
  /** As tipologias de `unitTypes` e de `pieceQuantityUnitTypes` que a obra não tem (mais). */
  readonly skippedUnitTypes: readonly string[];
  /**
   * Os serviços aprovados no orçamento que o envelope mudava (Fase 33, RN-438) — "FUNDAÇÃO ›
   * ESTACA". Ficaram como estavam: os itens, o índice e a perda, o código, a quantidade digitada,
   * a da peça importada, a da planilha, e a taxa de aço da disciplina deles.
   *
   * Pulados como os `skipped`, e não recusados com o envelope, pela mesma razão: ele grava
   * sozinho a cada pausa, e a mudança num aprovado vinda de uma aba aberta antes da aprovação
   * travaria todas as gravações seguintes dela, até alguém desfazê-la. O resto do envelope grava,
   * e a tela diz o que não gravou.
   */
  readonly lockedServices: readonly string[];
}

export class SaveProjectServiceItems {
  constructor(private readonly deps: ServiceItemsDeps) {}

  async execute(input: unknown, session: Session): Promise<Result<ServiceItemsSaved, AppError>> {
    const parsed = parseInput(saveProjectServiceItemsSchema, input);
    if (!parsed.ok) return parsed;

    const projectId = asId<'Project'>(parsed.value.projectId) as ProjectId;
    const allowed = this.deps.permissions.ensureForProject(
      session,
      PERMISSION.TAKEOFF_MANAGE,
      projectId,
    );
    if (!allowed.ok) return allowed;

    return this.deps.tx.run(() => this.gravar(projectId, parsed.value, session));
  }

  private async gravar(
    projectId: ProjectId,
    entrada: Entrada,
    session: Session,
  ): Promise<Result<ServiceItemsSaved, AppError>> {
    const agora = this.deps.clock.nowIso();
    const [
      services,
      disciplines,
      existentes,
      sheetImport,
      sheetQuantities,
      referenceSheets,
      decisoes,
      decisoesDeServico,
      unitTypes,
      materials,
      roles,
      links,
      taxasGravadas,
      pecasGravadas,
      aprovados,
    ] = await Promise.all([
      this.deps.services.listAvailableForProject(projectId),
      this.deps.disciplines.listAvailableForProject(projectId),
      this.deps.items.listByProject(projectId),
      this.deps.sheets.findImport(projectId),
      this.deps.sheets.listQuantities(projectId),
      this.deps.unitTypeItems.listReferenceSheets(projectId),
      this.deps.unitTypeItems.listQuantities(projectId),
      this.deps.unitTypeItems.listServiceQuantities(projectId),
      this.deps.unitTypes.listByProject(projectId),
      this.deps.materials.listAll(),
      this.deps.jobRoles.listAll(),
      this.deps.roleDisciplines.listAll(),
      this.deps.steelRates.listByProject(projectId),
      this.deps.pieceQuantities.listByProject(projectId),
      this.deps.budgets.listApprovedServices(projectId),
    ]);

    const rules: ServiceItemRules = {
      materialByCode: new Map<string, MaterialId>(
        materials
          .filter((material) => material.deletedAt === undefined)
          .map((material) => [materialNaturalKey(material.code), material.id]),
      ),
      activeRoles: new Set<JobRoleId>(
        roles.filter((role) => role.isActive && role.deletedAt === undefined).map((r) => r.id),
      ),
      roleDisciplines: JobRoleDisciplineIndex.build(links),
    };

    const servicoPorChave = servicosPelaChave(services, disciplines);
    // Os serviços aprovados no orçamento (Fase 33, RN-438): o que o envelope muda neles não
    // grava, e eles voltam em `lockedServices`. As taxas de aço da disciplina que tem serviço de
    // aço aprovado também ficam.
    const travados = servicosTravados(services, disciplines, aprovados);
    const acoTravado = acoTravadoPorDisciplina(services, disciplines, aprovados);
    const recusados = new Map<string, ApprovedService>();
    const tipologiasDaObra = new Set<string>(
      unitTypes.filter((unitType) => unitType.deletedAt === undefined).map((u) => u.id),
    );
    const skippedUnitTypes = entrada.unitTypes
      .map((tipologia) => tipologia.unitTypeId)
      .filter((id) => !tipologiasDaObra.has(id));
    const escopo = new Set<UnitTypeId>(
      entrada.unitTypes
        .map((tipologia) => asId<'UnitType'>(tipologia.unitTypeId) as UnitTypeId)
        .filter((id) => tipologiasDaObra.has(id)),
    );
    // As tipologias cujas quantidades por peça o envelope descreve (Fase 28, RN-390). É um
    // escopo à parte do `unitTypes`, e não o mesmo: a tela que ainda não conhece a planilha
    // por peça manda as tipologias de sempre, e **ausente não mexe** — senão a primeira
    // gravação dela apagaria o que outra tela importou.
    const escopoDasPecas = new Set<UnitTypeId>(
      (entrada.pieceQuantityUnitTypes ?? [])
        .map((id) => asId<'UnitType'>(id) as UnitTypeId)
        .filter((id) => tipologiasDaObra.has(id)),
    );
    for (const id of entrada.pieceQuantityUnitTypes ?? []) {
      if (!tipologiasDaObra.has(id) && !skippedUnitTypes.includes(id)) skippedUnitTypes.push(id);
    }

    // ── Os itens, serviço a serviço ───────────────────────────────────────────────
    const vivosPorServico = agrupar(existentes, (item) => item.serviceId);
    const tocados = new Set<ServiceId>();
    const skippedServices: string[] = [];
    const created: ServiceItem[] = [];
    const updated: ServiceItem[] = [];
    const removed: ServiceItem[] = [];
    const decididas: UnitTypeItemQuantity[] = [];
    const decididosDoServico: UnitTypeServiceQuantity[] = [];
    const quantidadesDoServico: ServiceSheetQuantities[] = [];
    const pecasDoEnvelope: PecaDoEnvelope[] = [];

    // O que está gravado de cada serviço travado, para a trava comparar com o envelope.
    const servicoDoItem = new Map<ServiceItemId, ServiceId>(
      existentes.map((item) => [item.id, item.serviceId]),
    );
    const gravadoDe = (serviceId: ServiceId): GravadoDoServico => {
      const quantidades = sheetQuantities.find((linha) => linha.serviceId === serviceId);
      return {
        itens: vivosPorServico.get(serviceId) ?? [],
        decisoes: decisoes.filter(
          (decisao) => servicoDoItem.get(decisao.serviceItemId) === serviceId,
        ),
        decisoesDoServico: decisoesDeServico.filter((decisao) => decisao.serviceId === serviceId),
        pecas: pecasGravadas.filter((peca) => peca.serviceId === serviceId),
        ...(quantidades === undefined ? {} : { quantidades }),
      };
    };

    for (const disciplina of entrada.disciplines) {
      for (const servico of disciplina.services) {
        const service = servicoPorChave.get(serviceKey(disciplina.name, servico.name));
        if (service === undefined) {
          skippedServices.push(`${disciplina.name} > ${servico.name}`);
          continue;
        }
        // O mesmo serviço duas vezes no envelope — pela disciplina da obra e pela global
        // de mesmo nome — é o mesmo serviço, e a segunda descrição apagaria a primeira.
        if (tocados.has(service.id)) continue;
        tocados.add(service.id);

        const plano = planejarServico({
          projectId,
          disciplina: disciplina.name,
          servico,
          service,
          existentes: vivosPorServico.get(service.id) ?? [],
          rules,
          escopo,
          escopoDasPecas,
          agora,
          session,
          newId: () => this.deps.ids.next<'ServiceItem'>(),
        });
        if (!plano.ok) return plano;

        // O serviço aprovado fica como está (Fase 33, RN-438). Se o envelope o mudava, ele entra
        // na lista dos recusados; se não, só o vínculo do item com o cadastro de materiais segue
        // — é o servidor que o deduz do código, e ninguém mexeu na quantidade por ele.
        const trava = travados.get(service.id);
        if (trava !== undefined) {
          if (mudaOServico(plano.value, gravadoDe(service.id), escopo, escopoDasPecas)) {
            recusados.set(trava.key, trava);
          } else {
            updated.push(...plano.value.itens.updated);
          }
          continue;
        }

        created.push(...plano.value.itens.created);
        updated.push(...plano.value.itens.updated);
        removed.push(...plano.value.itens.removed);
        decididas.push(...plano.value.decididas);
        decididosDoServico.push(...plano.value.decididosDoServico);
        pecasDoEnvelope.push(...plano.value.pecas);
        if (plano.value.quantidades !== undefined) {
          quantidadesDoServico.push(plano.value.quantidades);
        }
      }
    }

    // O serviço aprovado que o envelope não trouxe também fica como está: tirá-lo do catálogo da
    // tela é tirar os itens dele, e é aqui que isso chega ao servidor (RN-438).
    for (const [serviceId, trava] of travados) {
      if (tocados.has(serviceId)) continue;
      const gravado = gravadoDe(serviceId);
      if (mudaOServico(semNada(gravado.itens), gravado, escopo, escopoDasPecas)) {
        recusados.set(trava.key, trava);
      }
    }

    // ── As taxas de aço, disciplina a disciplina (Fase 27, RN-367) ───────────────
    //
    // Pelo mesmo mapa de nomes que acha o serviço: a disciplina da obra vence a global de
    // mesmo nome. Só a disciplina que trouxe `steelRates` é tocada — ausente não mexe, e é o
    // que deixa a tela antiga gravar sem apagar a taxa de ninguém; vazia apaga.
    const disciplinaPorNome = disciplinasPeloNome(disciplines);
    const taxasPorDisciplina = agrupar(taxasGravadas, (taxa) => taxa.disciplineId);
    const taxasTocadas = new Set<DisciplineId>();
    const taxasCriadas: DisciplineSteelRate[] = [];
    const taxasMudadas: DisciplineSteelRate[] = [];
    const taxasSaidas: DisciplineSteelRate[] = [];
    for (const disciplina of entrada.disciplines) {
      if (disciplina.steelRates === undefined && disciplina.elementSteelRates === undefined) {
        continue;
      }
      const alvo = disciplinaPorNome.get(normalizeName(disciplina.name));
      if (alvo === undefined) {
        skippedServices.push(`${disciplina.name} > taxas de aço`);
        continue;
      }
      if (taxasTocadas.has(alvo.id)) continue;
      taxasTocadas.add(alvo.id);
      const gravadas = taxasPorDisciplina.get(alvo.id) ?? [];
      // As listas que o envelope trouxe: a da disciplina inteira e, quando vieram, as por elemento
      // (Fase 42, RN-573) — a do elemento gravado que não veio sai, vazia.
      const listas: {
        readonly element?: AppliesTo;
        readonly rates: typeof disciplina.steelRates;
      }[] = [];
      if (disciplina.steelRates !== undefined) listas.push({ rates: disciplina.steelRates });
      if (disciplina.elementSteelRates !== undefined) {
        const vieram = new Set<AppliesTo>();
        for (const lista of disciplina.elementSteelRates) {
          if (vieram.has(lista.element)) continue;
          vieram.add(lista.element);
          listas.push({ element: lista.element, rates: lista.rates });
        }
        for (const taxa of gravadas) {
          if (taxa.element === undefined || vieram.has(taxa.element)) continue;
          vieram.add(taxa.element);
          listas.push({ element: taxa.element, rates: [] });
        }
      }
      const planos: SteelRatesPlan[] = [];
      for (const lista of listas) {
        const conferidas = normalizeSteelRates(lista.rates ?? []);
        if (!conferidas.ok) return err(AppError.fromDomain(conferidas.error));
        planos.push(
          planSteelRates({
            projectId,
            disciplineId: alvo.id,
            ...(lista.element === undefined ? {} : { element: lista.element }),
            existing: gravadas,
            incoming: conferidas.value,
            now: agora,
            userId: session.userId,
            newId: () => this.deps.ids.next<'DisciplineSteelRate'>(),
          }),
        );
      }
      // A taxa é da disciplina, e o serviço de aço aprovado dela a lê (RN-438): ela fica como
      // está — todas as listas dela —, e a recusa diz o serviço que a segura.
      const acoAprovado = acoTravado.get(normalizeName(alvo.name)) ?? [];
      if (acoAprovado.length > 0) {
        if (
          planos.some(
            (plano) => plano.created.length + plano.updated.length + plano.removed.length > 0,
          )
        ) {
          for (const trava of acoAprovado) recusados.set(trava.key, trava);
        }
        continue;
      }
      for (const plano of planos) {
        taxasCriadas.push(...plano.created);
        taxasMudadas.push(...plano.updated);
        taxasSaidas.push(...plano.removed);
      }
    }

    // O serviço que o envelope não trouxe perdeu os itens: é o retrato inteiro da obra, e
    // o que ficou de fora é o que alguém tirou (RN-255).
    // O aprovado não: fica com os itens (RN-438).
    for (const [serviceId, itens] of vivosPorServico) {
      if (tocados.has(serviceId) || travados.has(serviceId)) continue;
      removed.push(
        ...itens.map((item): ServiceItem => ({ ...item, updatedAt: agora, deletedAt: agora })),
      );
    }

    // ── As decisões das tipologias ────────────────────────────────────────────────
    // O item que saiu leva junto o número que alguém decidiu para ele: número pendurado
    // em item que não existe ressuscitaria, com cara de decisão, no dia em que alguém
    // recadastrasse o mesmo nome.
    //
    // O serviço aprovado fica de fora das comparações, nos dois lados: o envelope não trouxe nada
    // dele (o que trouxe foi conferido acima), e o gravado não pode sair por não ter vindo.
    const saiu = new Set<ServiceItemId>(removed.map((item) => item.id));
    const aberto = (serviceId: ServiceId | undefined) =>
      serviceId === undefined || !travados.has(serviceId);
    const { set, cleared } = diffItemQuantities({
      scope: escopo,
      before: decisoes.filter((decisao) => aberto(servicoDoItem.get(decisao.serviceItemId))),
      after: decididas,
    });
    const orfas = decisoes.filter(
      (decisao) => saiu.has(decisao.serviceItemId) && !escopo.has(decisao.unitTypeId),
    );
    const apagar = [...cleared, ...orfas];
    // A decisão de serviço não tem órfã: o serviço é do cadastro e não sai com o envelope.
    // Dentro do escopo, a que não veio é a que voltou ao traçado.
    const doServico = diffServiceQuantities({
      scope: escopo,
      before: decisoesDeServico.filter((decisao) => aberto(decisao.serviceId)),
      after: decididosDoServico,
    });

    // ── As quantidades por peça (Fase 28, RN-386, RN-390) ─────────────────────────
    //
    // Calculadas antes da primeira escrita: a peça repetida recusa o envelope inteiro, e
    // nada pode ter sido gravado quando isso acontece.
    //
    // A tipologia do escopo é trocada inteira pelo que veio. As peças do serviço aprovado entram
    // como estão gravadas, e assim a troca as mantém (RN-438).
    const pecas = planoDasPecas({
      projectId,
      escopo: escopoDasPecas,
      antes: pecasGravadas,
      depois: [
        ...pecasDoEnvelope,
        ...pecasGravadas
          .filter((peca) => !aberto(peca.serviceId) && escopoDasPecas.has(peca.unitTypeId))
          .map((peca): PecaDoEnvelope => ({
            unitTypeId: peca.unitTypeId,
            pieceName: peca.pieceName,
            serviceId: peca.serviceId,
            quantity: peca.quantity,
            onde: peca.pieceName,
            ...(peca.serviceItemId === undefined ? {} : { serviceItemId: peca.serviceItemId }),
          })),
      ],
      saiu,
      agora,
      session,
      newId: () => this.deps.ids.next<'PieceQuantity'>(),
    });
    if (!pecas.ok) return pecas;

    // ── A escrita, na ordem que os índices únicos exigem ──────────────────────────
    //
    // Primeiro o que sai, depois o que muda, por fim o que nasce: um item novo que
    // herda o material de um que saiu esbarraria no índice se entrasse antes. E o que
    // muda de material ou de função passa por uma escrita sem os dois — dois itens
    // trocando de material entre si colidiriam no meio da troca.
    if (apagar.length > 0) await this.deps.unitTypeItems.deleteQuantities(apagar);
    if (removed.length > 0) await this.deps.items.saveMany(removed);
    const anteriores = new Map(existentes.map((item) => [item.id, item]));
    const trocando = updated.filter((item) => {
      const antes = anteriores.get(item.id);
      return antes?.materialId !== item.materialId || antes?.jobRoleId !== item.jobRoleId;
    });
    if (trocando.length > 0) {
      await this.deps.items.saveMany(trocando.map(semVinculos));
    }
    if (updated.length > 0) await this.deps.items.saveMany(updated);
    if (created.length > 0) await this.deps.items.saveMany(created);
    if (set.length > 0) await this.deps.unitTypeItems.saveQuantities(set);
    if (doServico.cleared.length > 0) {
      await this.deps.unitTypeItems.deleteServiceQuantities(doServico.cleared);
    }
    if (doServico.set.length > 0) {
      await this.deps.unitTypeItems.saveServiceQuantities(doServico.set);
    }
    // A quantidade por peça aponta o item: entra depois dele, como a decisão da tipologia.
    if (pecas.value.itensOrfaos.length > 0) {
      await this.deps.pieceQuantities.deleteForItems(projectId, pecas.value.itensOrfaos);
    }
    if (pecas.value.trocar.length > 0) {
      await this.deps.pieceQuantities.replaceForUnitTypes(
        projectId,
        pecas.value.trocar,
        pecas.value.linhas,
      );
    }

    const planilha = planoDaPlanilha({
      projectId,
      entrada,
      agora,
      session,
      anterior: sheetImport,
      antes: sheetQuantities.filter((linha) => aberto(linha.serviceId)),
      depois: quantidadesDoServico,
    });
    if (planilha.apagarRegistro) await this.deps.sheets.deleteImport(projectId);
    if (planilha.registro !== undefined) await this.deps.sheets.saveImport(planilha.registro);
    if (planilha.gravarQuantidades.length > 0) {
      await this.deps.sheets.saveQuantities(planilha.gravarQuantidades);
    }
    if (planilha.apagarQuantidades.length > 0) {
      await this.deps.sheets.deleteQuantities(projectId, planilha.apagarQuantidades);
    }

    const abas = planoDasAbas({
      projectId,
      entrada,
      escopo,
      agora,
      session,
      antes: referenceSheets,
    });
    for (const aba of abas.gravar) await this.deps.unitTypeItems.saveReferenceSheet(aba);
    for (const unitTypeId of abas.apagar) {
      await this.deps.unitTypeItems.deleteReferenceSheet(unitTypeId);
    }

    // As taxas: primeiro a que sai, para a bitola que volta não esbarrar no índice parcial.
    const taxasEscritas = [...taxasSaidas, ...taxasMudadas, ...taxasCriadas];
    if (taxasEscritas.length > 0) await this.deps.steelRates.saveMany(taxasEscritas);

    const resumo: ServiceItemsSaved = {
      itemsCreated: created.length,
      itemsUpdated: updated.length,
      itemsRemoved: removed.length,
      quantitiesSet: set.length,
      quantitiesCleared: apagar.length,
      serviceQuantitiesSet: doServico.set.length,
      serviceQuantitiesCleared: doServico.cleared.length,
      steelRatesSet: taxasCriadas.length + taxasMudadas.length,
      steelRatesCleared: taxasSaidas.length,
      pieceQuantitiesSet: pecas.value.gravadas,
      pieceQuantitiesCleared: pecas.value.apagadas,
      skippedServices,
      skippedUnitTypes,
      lockedServices: [...recusados.values()].map(approvedServiceLabel),
    };

    // Reenviar o envelope igual não é acontecimento: a trilha registra o que mudou.
    const abasMudaram = abas.gravar.length + abas.apagar.length > 0;
    const mudou =
      created.length + updated.length + removed.length + set.length + apagar.length > 0 ||
      doServico.set.length + doServico.cleared.length > 0 ||
      taxasEscritas.length > 0 ||
      pecas.value.gravadas + pecas.value.apagadas > 0 ||
      planilha.mudou ||
      abasMudaram;
    if (mudou) {
      await this.deps.audit.record({
        action: 'UPDATE',
        entityType: 'ServiceItems',
        entityId: projectId,
        actorUserId: session.userId,
        projectId,
        metadata: {
          ...resumo,
          sheetChanged: planilha.mudou,
          referenceSheetsChanged: abasMudaram,
        },
      });
    }

    return ok(resumo);
  }
}

/**
 * O que muda na planilha da obra — calculado, e não gravado, aqui.
 *
 * As duas funções de plano abaixo são **síncronas** de propósito, e não por estilo: no
 * IndexedDB, esperar uma função assíncrona que não chega a falar com o banco deixa a
 * transação sem pedido pendente, e ela se fecha sozinha no meio do caso de uso. Quem
 * grava é o `gravar`, com o `await` direto no repositório.
 */
function planoDaPlanilha(input: {
  projectId: ProjectId;
  entrada: Entrada;
  agora: string;
  session: Session;
  anterior: ServiceSheetImport | undefined;
  antes: readonly ServiceSheetQuantities[];
  depois: readonly ServiceSheetQuantities[];
}): {
  readonly apagarRegistro: boolean;
  readonly registro?: ServiceSheetImport;
  readonly gravarQuantidades: readonly ServiceSheetQuantities[];
  readonly apagarQuantidades: readonly ServiceId[];
  readonly mudou: boolean;
} {
  const { sheetImport } = input.entrada;
  const nova: ServiceSheetImport | undefined =
    sheetImport === undefined
      ? undefined
      : {
          projectId: input.projectId,
          sources: sheetImport.sources,
          updatedAt: input.agora,
          updatedByUserId: input.session.userId,
          ...(sheetImport.fileName === undefined ? {} : { fileName: sheetImport.fileName }),
          ...(sheetImport.importedAt === undefined ? {} : { importedAt: sheetImport.importedAt }),
        };
  const apagarRegistro = nova === undefined && input.anterior !== undefined;
  const registro = nova !== undefined && !mesmaPlanilha(input.anterior, nova) ? nova : undefined;

  const antes = new Map(input.antes.map((linha) => [linha.serviceId, linha]));
  const gravarQuantidades = input.depois.filter((linha) => {
    const anterior = antes.get(linha.serviceId);
    return (
      anterior === undefined ||
      anterior.sheetUnit !== linha.sheetUnit ||
      !mesmasQuantidades(anterior.quantityBySheet, linha.quantityBySheet)
    );
  });
  const ficam = new Set<ServiceId>(input.depois.map((linha) => linha.serviceId));
  const apagarQuantidades = input.antes
    .filter((linha) => !ficam.has(linha.serviceId))
    .map((linha) => linha.serviceId);

  return {
    apagarRegistro,
    gravarQuantidades,
    apagarQuantidades,
    mudou:
      apagarRegistro ||
      registro !== undefined ||
      gravarQuantidades.length > 0 ||
      apagarQuantidades.length > 0,
    ...(registro === undefined ? {} : { registro }),
  };
}

/** A aba de referência de cada tipologia do escopo — o que grava e o que apaga. */
function planoDasAbas(input: {
  projectId: ProjectId;
  entrada: Entrada;
  escopo: ReadonlySet<UnitTypeId>;
  agora: string;
  session: Session;
  antes: readonly UnitTypeReferenceSheet[];
}): { readonly gravar: readonly UnitTypeReferenceSheet[]; readonly apagar: readonly UnitTypeId[] } {
  const antes = new Map(input.antes.map((aba) => [aba.unitTypeId, aba.sheetName]));
  const gravar: UnitTypeReferenceSheet[] = [];
  const apagar: UnitTypeId[] = [];

  for (const tipologia of input.entrada.unitTypes) {
    const unitTypeId = asId<'UnitType'>(tipologia.unitTypeId) as UnitTypeId;
    if (!input.escopo.has(unitTypeId)) continue;
    const anterior = antes.get(unitTypeId);
    if (anterior === tipologia.sheetName) continue;

    if (tipologia.sheetName === undefined) {
      apagar.push(unitTypeId);
    } else {
      gravar.push({
        unitTypeId,
        projectId: input.projectId,
        sheetName: tipologia.sheetName,
        updatedAt: input.agora,
        updatedByUserId: input.session.userId,
      });
    }
  }
  return { gravar, apagar };
}

/** Uma quantidade por peça como o envelope a trouxe, já com os ids do servidor. */
interface PecaDoEnvelope {
  readonly unitTypeId: UnitTypeId;
  readonly pieceName: string;
  readonly serviceId: ServiceId;
  readonly serviceItemId?: ServiceItemId;
  readonly quantity: number;
  /** "BLOCO > CORTE E DOBRA DE FERRAGEM > CA 50 - 8 MM" — para dizer onde está a repetida. */
  readonly onde: string;
}

/**
 * A chave de uma quantidade por peça: tipologia, serviço, item e o nome da peça como a tela o
 * compara (`nomeDaPeca`: sem espaço sobrando e sem caixa). É mais estrita que o índice único da
 * `0064` (`lower(btrim(...))`), e é por isso que o banco nunca chega a recusar o que passou daqui.
 */
function chaveDaPeca(peca: {
  readonly unitTypeId: UnitTypeId;
  readonly serviceId: ServiceId;
  readonly serviceItemId?: ServiceItemId;
  readonly pieceName: string;
}): string {
  return [
    peca.unitTypeId,
    peca.serviceId,
    peca.serviceItemId ?? '',
    nomeDaPeca(peca.pieceName),
  ].join('|');
}

// ── Um serviço do envelope ──────────────────────────────────────────────────────

type ServicoDoEnvelope = Entrada['disciplines'][number]['services'][number];

/** O que o envelope diz de um serviço, já com os ids do servidor — ainda não gravado. */
interface PlanoDoServico {
  readonly itens: ServiceItemPlan;
  readonly decididas: readonly UnitTypeItemQuantity[];
  readonly decididosDoServico: readonly UnitTypeServiceQuantity[];
  readonly pecas: readonly PecaDoEnvelope[];
  /** A unidade e as quantidades da planilha, quando o envelope traz alguma. */
  readonly quantidades?: ServiceSheetQuantities;
}

/**
 * O plano de um serviço do envelope: os itens, a quantidade de cada tipologia, a das peças e a da
 * planilha. Síncrono pelo motivo de `planoDaPlanilha`.
 *
 * As decisões e as peças apontam o id do item **gravado** — o que acabou de nascer inclusive.
 */
function planejarServico(input: {
  projectId: ProjectId;
  disciplina: string;
  servico: ServicoDoEnvelope;
  service: Service;
  existentes: readonly ServiceItem[];
  rules: ServiceItemRules;
  escopo: ReadonlySet<UnitTypeId>;
  escopoDasPecas: ReadonlySet<UnitTypeId>;
  agora: string;
  session: Session;
  newId: () => ServiceItemId;
}): Result<PlanoDoServico, AppError> {
  const { projectId, servico, service, escopo, escopoDasPecas, agora, session } = input;
  const plano = planServiceItems({
    projectId,
    service,
    existing: input.existentes,
    incoming: servico.items.map((item) => ({
      description: item.description,
      kind: item.kind,
      kindOrigin: item.kindOrigin,
      quantityBySheet: item.quantityBySheet,
      ...(item.code === undefined ? {} : { purchaseCode: item.code }),
      ...(item.unit === undefined ? {} : { unit: item.unit }),
      ...(item.payment === undefined ? {} : { payment: item.payment }),
      ...(item.jobRoleId === undefined ? {} : { jobRoleId: asId<'JobRole'>(item.jobRoleId) }),
      ...(item.consumptionIndex === undefined ? {} : { consumptionIndex: item.consumptionIndex }),
      ...(item.indexOrigin === undefined ? {} : { indexOrigin: item.indexOrigin }),
      ...(item.indexAverage === undefined ? {} : { indexAverage: item.indexAverage }),
      ...(item.lossPercent === undefined ? {} : { lossPercent: item.lossPercent }),
      ...(item.steelGauge === undefined ? {} : { steelGauge: item.steelGauge }),
    })),
    rules: input.rules,
    now: agora,
    newId: input.newId,
  });
  if (!plano.ok) return err(AppError.fromDomain(plano.error));

  const decididas: UnitTypeItemQuantity[] = [];
  const decididosDoServico: UnitTypeServiceQuantity[] = [];
  const pecas: PecaDoEnvelope[] = [];

  for (const [indice, item] of servico.items.entries()) {
    const gravado = plano.value.items[indice]!;
    for (const decisao of item.quantities) {
      const unitTypeId = asId<'UnitType'>(decisao.unitTypeId) as UnitTypeId;
      // Decisão de tipologia fora do escopo não entra: ou a tipologia não é desta
      // obra, ou o envelope não disse que a descrevia — e aí não é dele trocá-la.
      if (!escopo.has(unitTypeId)) continue;
      const quantidade = validItemQuantity(decisao.quantity, gravado.description);
      if (!quantidade.ok) return err(AppError.fromDomain(quantidade.error));
      decididas.push({
        unitTypeId,
        serviceItemId: gravado.id,
        projectId,
        quantity: quantidade.value,
        updatedAt: agora,
        updatedByUserId: session.userId,
        // O motivo de discordar do calculado (D9). Quem o exige é a tela, que tem o
        // desenho e o índice; aqui ele é guardado ao lado do número.
        ...(decisao.reason === undefined ? {} : { reason: decisao.reason }),
      });
    }

    // A quantidade pronta de cada peça (Fase 28, RN-384), pelo id do item **gravado**
    // — o que acabou de nascer inclusive, como a decisão da tipologia logo acima.
    for (const peca of item.pieceQuantities ?? []) {
      const unitTypeId = asId<'UnitType'>(peca.unitTypeId) as UnitTypeId;
      if (!escopoDasPecas.has(unitTypeId)) continue;
      const quantidade = validItemQuantity(peca.quantity, gravado.description);
      if (!quantidade.ok) return err(AppError.fromDomain(quantidade.error));
      pecas.push({
        unitTypeId,
        pieceName: peca.pieceName,
        serviceId: service.id,
        serviceItemId: gravado.id,
        quantity: quantidade.value,
        onde: `${input.disciplina} > ${servico.name} > ${gravado.description}`,
      });
    }
  }

  // A quantidade do serviço em cada peça (RN-388): os kg da MONTAGEM, o m³ da
  // CONCRETAGEM — na unidade do serviço, e sem item.
  for (const peca of servico.pieceQuantities ?? []) {
    const unitTypeId = asId<'UnitType'>(peca.unitTypeId) as UnitTypeId;
    if (!escopoDasPecas.has(unitTypeId)) continue;
    const quantidade = decideInServiceUnit(service, peca.quantity);
    if (!quantidade.ok) return err(AppError.fromDomain(quantidade.error));
    pecas.push({
      unitTypeId,
      pieceName: peca.pieceName,
      serviceId: service.id,
      quantity: quantidade.value,
      onde: `${input.disciplina} > ${servico.name}`,
    });
  }

  // O número do serviço em cada tipologia (fatia 5), conferido contra a peça que o
  // **servidor** tem para ele: é daqui que sai se a correção pede motivo (D4), e a tela
  // de outra versão não decide isso por nós.
  for (const decisao of servico.quantities ?? []) {
    const unitTypeId = asId<'UnitType'>(decisao.unitTypeId) as UnitTypeId;
    if (!escopo.has(unitTypeId)) continue;
    const medido =
      decisao.measured === undefined
        ? undefined
        : decideMeasured(service, decisao.measured, decisao.reason);
    if (medido !== undefined && !medido.ok) return err(AppError.fromDomain(medido.error));
    const naUnidade =
      decisao.quantity === undefined ? undefined : decideInServiceUnit(service, decisao.quantity);
    if (naUnidade !== undefined && !naUnidade.ok) {
      return err(AppError.fromDomain(naUnidade.error));
    }
    decididosDoServico.push({
      unitTypeId,
      serviceId: service.id,
      projectId,
      updatedAt: agora,
      updatedByUserId: session.userId,
      ...(medido === undefined ? {} : { measured: medido.value.measured }),
      ...(medido?.value.reason === undefined ? {} : { measuredReason: medido.value.reason }),
      ...(naUnidade === undefined ? {} : { quantity: naUnidade.value }),
    });
  }

  const comPlanilha = Object.keys(servico.quantityBySheet).length > 0 || servico.unit !== undefined;
  return ok({
    itens: plano.value,
    decididas,
    decididosDoServico,
    pecas,
    ...(comPlanilha
      ? {
          quantidades: {
            projectId,
            serviceId: service.id,
            quantityBySheet: servico.quantityBySheet,
            updatedAt: agora,
            ...(servico.unit === undefined ? {} : { sheetUnit: servico.unit }),
          },
        }
      : {}),
  });
}

/** O que está gravado de um serviço — o lado de lá da comparação da trava. */
interface GravadoDoServico {
  readonly itens: readonly ServiceItem[];
  /** As quantidades dos itens dele, em todas as tipologias. */
  readonly decisoes: readonly UnitTypeItemQuantity[];
  readonly decisoesDoServico: readonly UnitTypeServiceQuantity[];
  readonly pecas: readonly PieceQuantityRecord[];
  readonly quantidades?: ServiceSheetQuantities;
}

/** O plano do serviço que o envelope não trouxe: os itens saem, e nada mais vem. */
function semNada(itens: readonly ServiceItem[]): PlanoDoServico {
  return {
    itens: { created: [], updated: [], removed: itens, items: [] },
    decididas: [],
    decididosDoServico: [],
    pecas: [],
  };
}

/**
 * O envelope muda o serviço? (Fase 33, RN-438)
 *
 * É a pergunta da trava: o serviço aprovado fica como está, mas só a mudança de verdade entra
 * na lista dos recusados — reenviar o aprovado igual, que é o que a tela faz a cada gravação,
 * não é recusa nenhuma. Cada parte é comparada como a gravação a compararia, e dentro do mesmo
 * escopo de tipologias: a que o envelope não descreve não muda por ele.
 *
 * O vínculo do item com o cadastro de materiais não conta (`sameItemAsWritten`).
 */
function mudaOServico(
  plano: PlanoDoServico,
  gravado: GravadoDoServico,
  escopo: ReadonlySet<UnitTypeId>,
  escopoDasPecas: ReadonlySet<UnitTypeId>,
): boolean {
  const anteriores = new Map(gravado.itens.map((item) => [item.id, item]));
  const itensMudam =
    plano.itens.created.length > 0 ||
    plano.itens.removed.length > 0 ||
    plano.itens.updated.some((item) => {
      const anterior = anteriores.get(item.id);
      return anterior === undefined || !sameItemAsWritten(anterior, item);
    });
  if (itensMudam) return true;

  const doItem = diffItemQuantities({
    scope: escopo,
    before: gravado.decisoes,
    after: plano.decididas,
  });
  if (doItem.set.length + doItem.cleared.length > 0) return true;

  const doServico = diffServiceQuantities({
    scope: escopo,
    before: gravado.decisoesDoServico,
    after: plano.decididosDoServico,
  });
  if (doServico.set.length + doServico.cleared.length > 0) return true;

  const pecasAntes = new Map(
    gravado.pecas
      .filter((peca) => escopoDasPecas.has(peca.unitTypeId))
      .map((peca) => [chaveDaPeca(peca), peca]),
  );
  const pecasDepois = new Map(plano.pecas.map((peca) => [chaveDaPeca(peca), peca]));
  if (pecasAntes.size !== pecasDepois.size) return true;
  for (const [chave, peca] of pecasDepois) {
    const anterior = pecasAntes.get(chave);
    if (anterior?.quantity !== peca.quantity || anterior.pieceName !== peca.pieceName) return true;
  }

  const antes = gravado.quantidades;
  const depois = plano.quantidades;
  if (antes === undefined || depois === undefined) return antes !== depois;
  return (
    antes.sheetUnit !== depois.sheetUnit ||
    !mesmasQuantidades(antes.quantityBySheet, depois.quantityBySheet)
  );
}

/**
 * O que muda nas quantidades por peça (Fase 28, RN-386, RN-390) — calculado, e não gravado,
 * aqui; síncrono pelo motivo de `planoDaPlanilha`.
 *
 * Dentro do escopo (`pieceQuantityUnitTypes`), cada tipologia fica **exatamente** com o que
 * veio: a peça que não veio sai. Quem decide que a peça de fora da planilha nova continua (D5)
 * é a tela, que junta a planilha com o que a tipologia já tinha antes de mandar — o servidor
 * só guarda o retrato.
 *
 * Só é trocada a tipologia em que algo mudou: a bancada grava a cada pausa da digitação, e
 * regravar as milhares de linhas de uma Fundação a cada tecla seria trabalho sem notícia. A
 * linha que voltou igual volta com o id e a data de antes — a data diz quando **aquele**
 * número mudou, e não quando a tipologia foi regravada por causa de outro.
 *
 * Fora do escopo, só sai a linha do item que saiu do catálogo: o número pendurado num item
 * apagado contaria em lugar nenhum, como a decisão órfã da tipologia.
 *
 * A peça repetida no envelope — mesma tipologia, serviço, item e nome — recusa a gravação: dois
 * números para a mesma peça é a planilha dizendo duas coisas, e somar ou escolher um seria
 * decidir por quem a escreveu.
 */
function planoDasPecas(input: {
  projectId: ProjectId;
  escopo: ReadonlySet<UnitTypeId>;
  antes: readonly PieceQuantityRecord[];
  depois: readonly PecaDoEnvelope[];
  saiu: ReadonlySet<ServiceItemId>;
  agora: string;
  session: Session;
  newId: () => PieceQuantityRecord['id'];
}): Result<
  {
    readonly trocar: readonly UnitTypeId[];
    readonly linhas: readonly PieceQuantityRecord[];
    readonly itensOrfaos: readonly ServiceItemId[];
    readonly gravadas: number;
    readonly apagadas: number;
  },
  AppError
> {
  const vistas = new Set<string>();
  for (const peca of input.depois) {
    const chave = chaveDaPeca(peca);
    if (vistas.has(chave)) {
      return err(
        validationError(
          'TAKEOFF.PIECE_QUANTITY_DUPLICATED',
          `A peça ${peca.pieceName} aparece duas vezes em ${peca.onde}, na mesma tipologia. ` +
            'Cada peça tem um número só por serviço e item: junte as duas linhas antes de gravar.',
        ),
      );
    }
    vistas.add(chave);
  }

  const noEscopo = input.antes.filter((peca) => input.escopo.has(peca.unitTypeId));
  const anteriores = new Map(noEscopo.map((peca) => [chaveDaPeca(peca), peca]));
  const mudaram = new Set<UnitTypeId>();
  const linhas: PieceQuantityRecord[] = [];
  let gravadas = 0;
  for (const peca of input.depois) {
    const anterior = anteriores.get(chaveDaPeca(peca));
    if (
      anterior !== undefined &&
      anterior.quantity === peca.quantity &&
      anterior.pieceName === peca.pieceName
    ) {
      linhas.push(anterior);
      continue;
    }
    gravadas += 1;
    mudaram.add(peca.unitTypeId);
    linhas.push({
      id: anterior?.id ?? input.newId(),
      projectId: input.projectId,
      unitTypeId: peca.unitTypeId,
      pieceName: peca.pieceName,
      serviceId: peca.serviceId,
      quantity: peca.quantity,
      updatedAt: input.agora,
      updatedByUserId: input.session.userId,
      ...(peca.serviceItemId === undefined ? {} : { serviceItemId: peca.serviceItemId }),
    });
  }
  const sairam = noEscopo.filter((peca) => !vistas.has(chaveDaPeca(peca)));
  for (const peca of sairam) mudaram.add(peca.unitTypeId);

  const orfas = input.antes.filter(
    (peca) =>
      !input.escopo.has(peca.unitTypeId) &&
      peca.serviceItemId !== undefined &&
      input.saiu.has(peca.serviceItemId),
  );

  return ok({
    trocar: [...mudaram],
    linhas: linhas.filter((linha) => mudaram.has(linha.unitTypeId)),
    itensOrfaos: [...new Set(orfas.map((peca) => peca.serviceItemId!))],
    gravadas,
    apagadas: sairam.length + orfas.length,
  });
}

/**
 * O serviço de cada chave natural `(disciplina, serviço)` que a obra alcança.
 *
 * Duas disciplinas com o mesmo nome — a da obra e a global — dão a mesma chave. Vence a
 * **da obra**, pela mesma regra que o cadastro usa para achar a disciplina da planilha:
 * quem a cadastrou na obra o fez por algum motivo.
 */
export function servicosPelaChave(
  services: readonly Service[],
  disciplines: readonly Discipline[],
): ReadonlyMap<string, Service> {
  const disciplinaPorId = new Map(disciplines.map((disciplina) => [disciplina.id, disciplina]));
  const porChave = new Map<string, { service: Service; daObra: boolean }>();
  for (const service of services) {
    if (service.deletedAt !== undefined) continue;
    const disciplina = disciplinaPorId.get(service.disciplineId);
    if (disciplina === undefined) continue;
    const chave = serviceKey(disciplina.name, service.name);
    const daObra = disciplina.projectId !== undefined;
    const atual = porChave.get(chave);
    if (atual === undefined || (daObra && !atual.daObra)) porChave.set(chave, { service, daObra });
  }
  return new Map([...porChave.entries()].map(([chave, { service }]) => [chave, service]));
}

/**
 * A disciplina de cada nome normalizado que a obra alcança — a da obra vence a global de
 * mesmo nome, pela regra de `servicosPelaChave` (Fase 27: é por ela que a taxa de aço acha a
 * disciplina do envelope).
 */
function disciplinasPeloNome(disciplines: readonly Discipline[]): ReadonlyMap<string, Discipline> {
  const porNome = new Map<string, Discipline>();
  for (const disciplina of disciplines) {
    if (disciplina.deletedAt !== undefined) continue;
    const chave = normalizeName(disciplina.name);
    const atual = porNome.get(chave);
    const daObra = disciplina.projectId !== undefined;
    if (atual === undefined || (daObra && atual.projectId === undefined)) {
      porNome.set(chave, disciplina);
    }
  }
  return porNome;
}

/** O item sem material nem função — a escrita intermediária da troca. */
function semVinculos(item: ServiceItem): ServiceItem {
  const { materialId: _material, jobRoleId: _funcao, ...resto } = item;
  return resto;
}

/**
 * A mesma planilha, aba por aba.
 *
 * Campo a campo, e não por `JSON.stringify`: o `jsonb` devolve as chaves do objeto na
 * ordem dele, e não na que entraram — a comparação por texto acusaria mudança a cada
 * reenvio, e a trilha ganharia uma "troca de planilha" por gravação.
 */
function mesmaPlanilha(a: ServiceSheetImport | undefined, b: ServiceSheetImport): boolean {
  return (
    a !== undefined &&
    a.fileName === b.fileName &&
    a.importedAt === b.importedAt &&
    a.sources.length === b.sources.length &&
    a.sources.every((aba, indice) => {
      const outra = b.sources[indice]!;
      return (
        aba.name === outra.name &&
        aba.scope === outra.scope &&
        aba.imported === outra.imported &&
        aba.summary.disciplines === outra.summary.disciplines &&
        aba.summary.services === outra.summary.services &&
        aba.summary.items === outra.summary.items &&
        aba.summary.codes === outra.summary.codes
      );
    })
  );
}
